import { ColumnDef } from '@tanstack/react-table'
import { InspectionThumbnail } from 'components/InspectionThumbnail'
import { TagCell } from 'components/table/cells/TagCell'
import { TextCell } from 'components/table/cells/TextCell'
import { parseISO } from 'date-fns'
import { File } from 'lucide-react'
import styled from 'styled-components'
import {
  GeckoFileCategory,
  GeckoFileCategoryLabels,
  GeckoUnit,
  InputType,
} from 'types'

import { EntryActionCell } from './EntryActionsCell'
import { BasicComponent, FilesTableEntry } from './FilesTable'

const FileTypeContainer = styled.div<{ fileType: string }>`
  display: flex;
  flex-direction: column;
  width: 4rem;
  height: 4rem;
  background-color: ${({ fileType, theme }) => {
    if (fileType.toLowerCase() === 'pdf') {
      return theme.forecast.redGlassBackground
    } else if (['doc', 'docx'].includes(fileType.toLowerCase())) {
      return theme.forecast.blueGlassBackground
    } else if (['xls', 'xlsx', 'csv'].includes(fileType.toLowerCase())) {
      return theme.forecast.greenGlassBackground
    } else {
      return theme.forecast.glassBackground
    }
  }};
  border-radius: 0.25rem;
  margin: 0;
  justify-content: center;
  align-items: center;
  cursor: default;
`

const EntryActionCellWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const InspectionThumbnailContainer = styled.div`
  display: flex;
  flex-direction: column;
`
interface ReportTooltipProps {
  inspectionIds: string[]
  unitID: string
}
const ReportTooltip: React.FC<ReportTooltipProps> = ({
  inspectionIds,
  unitID,
}: ReportTooltipProps) => {
  return (
    <InspectionThumbnailContainer>
      {inspectionIds.map((inspectionId) => (
        <InspectionThumbnail
          key={inspectionId}
          inspectionID={inspectionId}
          unitID={unitID ?? ''}
          linkable={true}
        />
      ))}
    </InspectionThumbnailContainer>
  )
}

// here when we want to do bulk edit/selection we can add that as a columns
// for the reusable table we should pass this in as a prop
export const defaultFilesTableColumns = (
  unitID: string,
  forceRefresh: () => void,
  components: BasicComponent[],
  buttonColor?: string,
  unit?: GeckoUnit,
  refreshData?: () => Promise<void>,
  refreshThumbnails?: () => Promise<void>,
  setIsPrimaryImageLoaded?: (loaded: boolean) => void
): ColumnDef<FilesTableEntry>[] => {
  return [
    {
      accessorKey: 'fileType',
      id: 'fileType',
      header: 'File Type',
      sortUndefined: 'last',
      sortingFn: 'alphanumeric',
      filterFn: 'multiSelectFilter',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) => (
        <FileTypeContainer fileType={row.original.fileType}>
          <File />
          {row.original.fileType.toUpperCase()}
        </FileTypeContainer>
      ),
    },
    {
      accessorKey: 'visibleName',
      id: 'visibleName',
      header: 'File Name',
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.Text,
      },
      cell: ({ row }) =>
        row.original.visibleName.length > 50 ? (
          <TextCell truncate text={row.original.visibleName} />
        ) : (
          <TextCell text={row.original.visibleName} />
        ),
    },
    {
      accessorKey: 'uploadDate',
      id: 'uploadDate',
      header: 'Upload Date',
      sortingFn: 'datetime',
      sortUndefined: 'last',
      filterFn: 'dateRangeFilter',
      meta: {
        inputType: InputType.Date,
      },
      cell: ({ row }) => (
        <TextCell text={parseISO(row.original.uploadDate)?.toDateString()} />
      ),
    },
    {
      accessorKey: 'fileCategory',
      id: 'fileCategory',
      header: 'Category',
      sortUndefined: 'last',
      sortingFn: 'alphanumeric',
      filterFn: 'multiSelectFilter',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) => (
        <TagCell
          text={GeckoFileCategoryLabels[row.original.fileCategory]}
          color={buttonColor}
          tooltip={
            row.original.fileCategory === GeckoFileCategory.GECKO_REPORT && (
              <ReportTooltip
                inspectionIds={row.original.inspectionIds}
                unitID={unitID}
              />
            )
          }
        />
      ),
    },
    {
      accessorKey: 'componentNames',
      id: 'componentNames',
      header: 'Component Tag(s)',
      sortUndefined: 'last',
      sortingFn: 'alphanumeric',
      filterFn: 'multiSelectFilter',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) =>
        row.original.componentNames.length > 0 ? (
          row.original.componentNames.map((componentName) => (
            <TagCell
              key={componentName}
              text={componentName}
              color={buttonColor}
            />
          ))
        ) : (
          <TextCell />
        ),
    },
    {
      accessorKey: 'actions',
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <EntryActionCellWrapper>
          <EntryActionCell
            downloadFirstFile={true}
            deleteEntry={true}
            entryId={row.original.id}
            fileName={row.original.visibleName}
            fileCategory={row.original.fileCategory}
            forceRefresh={forceRefresh}
            components={components}
            unit={unit}
            refreshData={refreshData || (async () => {})}
            refreshThumbnails={refreshThumbnails || (async () => {})}
            setIsPrimaryImageLoaded={setIsPrimaryImageLoaded || (() => {})}
          />
        </EntryActionCellWrapper>
      ),
    },
  ]
}
